import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { MapPin, FileText, ArrowRight, ClipboardCheck } from 'lucide-react'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { getAssignments, type Visit } from '../../lib/services/visits'
import { getComplianceTrends, type ComplianceTrendPoint } from '../../lib/services/dashboards'
import { getCorrectiveActions, type CorrectiveAction } from '../../lib/services/correctiveActions'

interface ClientSummary {
  facility: FacilityOption
  latestScore: number | null
  cmsOutcome: ComplianceTrendPoint['cms_outcome'] | null
  openActions: number
  overdueActions: number
  lastVisitDate: string | null
}

function buildClientSummaries(
  facilities: FacilityOption[],
  trends: ComplianceTrendPoint[],
  actions: CorrectiveAction[],
  visits: Visit[],
): ClientSummary[] {
  return facilities.map(f => {
    const facilityVisits = visits.filter(v => v.facility_id === f.facility_id)
    const releasedVisits = facilityVisits.filter(v => v.status === 'released')
    const lastVisit = releasedVisits.sort((a, b) => (b.visit_date ?? '').localeCompare(a.visit_date ?? ''))[0]
    const facilityActions = actions.filter(a => a.visit?.facility_id === f.facility_id)
    const openActions = facilityActions.filter(a => a.status === 'open' || a.status === 'in_progress').length
    const overdueActions = facilityActions.filter(a => a.status === 'overdue').length

    // Use the latest trend point (trends are global, so score is approximate for now)
    const latestTrend = trends[trends.length - 1]

    return {
      facility: f,
      latestScore: latestTrend?.overall_score ?? null,
      cmsOutcome: latestTrend?.cms_outcome ?? null,
      openActions,
      overdueActions,
      lastVisitDate: lastVisit?.visit_date ?? null,
    }
  })
}

function scoreBarColor(score: number | null): string {
  if (score === null) return 'bg-slate-300'
  if (score >= 95) return 'bg-gradient-to-r from-teal-500 to-teal-400'
  if (score >= 85) return 'bg-gradient-to-r from-amber-500 to-yellow-400'
  return 'bg-gradient-to-r from-rose-500 to-pink-400'
}

function statusDot(outcome: ComplianceTrendPoint['cms_outcome'] | null): string {
  if (!outcome) return 'bg-slate-300'
  if (outcome === 'meets_standard') return 'bg-emerald-500'
  if (outcome === 'conditional') return 'bg-amber-500'
  return 'bg-rose-500'
}

export function ConsultantDashboard() {
  const facilitiesQuery = useQuery({
    queryKey: ['dashboard-facilities'],
    queryFn: getFacilities,
    staleTime: 5 * 60 * 1000,
  })

  const visitsQuery = useQuery({
    queryKey: ['dashboard-visits'],
    queryFn: getAssignments,
    staleTime: 2 * 60 * 1000,
  })

  const trendsQuery = useQuery({
    queryKey: ['dashboard-trends'],
    queryFn: () => getComplianceTrends(365),
    staleTime: 5 * 60 * 1000,
  })

  const actionsQuery = useQuery({
    queryKey: ['dashboard-actions'],
    queryFn: () => getCorrectiveActions(),
    staleTime: 2 * 60 * 1000,
  })

  const facilities = facilitiesQuery.data ?? []
  const visits = visitsQuery.data ?? []
  const trends = trendsQuery.data ?? []
  const actions = actionsQuery.data ?? []

  const clients = buildClientSummaries(facilities, trends, actions, visits)
  const upcomingVisits = visits.filter(v => v.status === 'scheduled').slice(0, 4)
  const totalOpen = actions.filter(a => a.status === 'open' || a.status === 'in_progress').length
  const totalOverdue = actions.filter(a => a.status === 'overdue').length

  const isLoading = facilitiesQuery.isLoading || visitsQuery.isLoading

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {/* Greeting */}
      <section className="v11-workspace-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Client Portfolio</p>
            <h1 className="mt-1 font-brand text-2xl font-bold text-[var(--brand-deep)]">
              Your Clients at a Glance
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {facilities.length} active client{facilities.length !== 1 ? 's' : ''} &middot; {totalOpen} open actions &middot; {totalOverdue} overdue
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/visits" className="btn-primary px-4 py-2 text-sm">
              <ClipboardCheck size={14} className="mr-1.5 inline" />
              Start Survey
            </Link>
            <Link to="/reports" className="btn-ghost px-4 py-2 text-sm">
              <FileText size={14} className="mr-1.5 inline" />
              Reports
            </Link>
          </div>
        </div>
      </section>

      <Link
        to="/visits"
        className="v11-dashboard-card block p-5"
      >
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Facility operations
        </p>
        <h2 className="mt-1 font-brand text-lg font-extrabold text-[var(--brand-deep)]">
          Select a facility to access facility-level modules
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Chart Audit, Insurance, Credentialing, PAT Intake, and Patient Communications stay scoped to the selected facility or visit.
        </p>
      </Link>

      {/* Client Cards */}
      {isLoading && <p className="text-center text-sm text-slate-500">Loading portfolio...</p>}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {clients.map(c => (
          <Link
            key={c.facility.facility_id}
            to={`/visits?facility=${c.facility.facility_id}`}
            className="v11-dashboard-card overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="font-brand text-sm font-bold text-[var(--brand-deep)]">{c.facility.facility_name}</h3>
              <span className={`h-2.5 w-2.5 rounded-full ${statusDot(c.cmsOutcome)}`} />
            </div>
            <div className="px-5 pb-2">
              <div className="flex gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin size={10} />
                  {c.facility.city ?? ''}{c.facility.state ? `, ${c.facility.state}` : ''}
                </span>
                {c.facility.facility_type && <span>{c.facility.facility_type}</span>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 px-5 pb-3">
              <div className="v11-mini-metric p-2">
                <p className="font-brand text-lg font-extrabold text-[var(--brand-deep)]">{c.latestScore ?? '—'}</p>
                <p className="text-[9px] font-bold uppercase text-slate-400">Score</p>
              </div>
              <div className="v11-mini-metric p-2">
                <p className="font-brand text-lg font-extrabold text-[var(--brand-deep)]">{c.openActions}</p>
                <p className="text-[9px] font-bold uppercase text-slate-400">Open</p>
              </div>
              <div className="v11-mini-metric p-2">
                <p className={`font-brand text-lg font-extrabold ${c.overdueActions > 0 ? 'text-rose-600' : 'text-[var(--brand-deep)]'}`}>{c.overdueActions}</p>
                <p className="text-[9px] font-bold uppercase text-slate-400">Overdue</p>
              </div>
            </div>
            <div className="px-5 pb-1">
              <div className="h-1 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${scoreBarColor(c.latestScore)}`} style={{ width: `${c.latestScore ?? 0}%` }} />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-2.5">
              <span className="text-[10px] text-slate-400">Last visit: {c.lastVisitDate?.slice(0, 10) ?? 'None'}</span>
              <span className="text-[10px] font-bold text-[var(--brand-primary)]">View <ArrowRight size={8} className="inline" /></span>
            </div>
          </Link>
        ))}
      </section>

      {/* Upcoming Visits */}
      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="font-brand text-sm font-bold text-[var(--brand-deep)]">Upcoming Visits</h2>
          <Link to="/visits" className="text-xs font-bold text-[var(--brand-primary)] hover:underline">
            View all <ArrowRight size={10} className="inline" />
          </Link>
        </div>
        <div className="p-2">
          {upcomingVisits.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-slate-400">No upcoming visits</p>
          )}
          {upcomingVisits.map(v => (
            <Link key={v.id} to={`/visits/${v.id}`} className="v11-list-link">
              <div className="flex items-center gap-2.5">
                <MapPin size={14} className="text-[var(--brand-primary)]" />
                <div>
                  <p className="text-sm font-bold text-slate-800">{v.facility_name}</p>
                  <p className="text-xs text-slate-400">{v.visit_type ?? 'Visit'}</p>
                </div>
              </div>
              <span className="badge-scheduled">{v.visit_date?.slice(5) ?? 'TBD'}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
